import { Trans, useLingui } from '@lingui/react/macro';

import Button from '@/design_system/Button';
import { Footer, FooterButtons } from '@/design_system/Footer';
import { useShowToast } from '@/design_system/Toast';
import { useStartRepair } from '@/models/article';
import { useRequestContext } from '@/routes/Requests/contexts/RequestContext';

import { RepairDuration } from '../RepairDuration/RepairDuration';

import { ActionSeparator } from './ActionSeparator';
import { ArticleActionProps } from './ArticleActions';

export const StartArticleRepairAction = ({ article, onActionDone }: ArticleActionProps) => {
  const { t } = useLingui();
  const showToast = useShowToast();
  const { workflow } = useRequestContext();

  const {
    mutateAsync: startRepair,
    isPending: isStartRepairPending,
    isSuccess: isStartRepairSuccess,
  } = useStartRepair({
    articleId: article.id,
  });

  const confirmStartRepair = async () => {
    try {
      await startRepair();
      onActionDone();
    } catch (err: any) {
      showToast({
        type: 'error',
        text:
          err.message ??
          t({
            id: 'article.actions.start_article_repair.error',
            message: 'An error occurred while starting article repair. Please try again later.',
          }),
      });
    }
  };

  const repairStep = workflow?.nodes.find((node) => 'step' in node && node.step === 'repair');

  return (
    <Footer>
      <FooterButtons>
        {repairStep?.config?.editRepairDueDays?.enabled && (
          <>
            <RepairDuration />
            <ActionSeparator />
          </>
        )}
        <Button
          variant="primary"
          size="medium"
          onPress={() => {
            confirmStartRepair();
          }}
          isLoading={isStartRepairPending || isStartRepairSuccess}
        >
          <Trans id="article.actions.start_article_repair.validate">Start repair</Trans>
        </Button>
      </FooterButtons>
    </Footer>
  );
};
